export default class DrawControls {
  constructor({
    polygonClassName,
    selectClassName,
    editClassName,
    deleteVerticesClassName,
    draw,
    selectedColor = "#007bff",
    onDrawModeChange = () => {
      /* do nothing */
    },
  }) {
    this._selectClassName = selectClassName;
    this._polygonClassName = polygonClassName;
    this._editClassName = editClassName;
    this._deleteVerticesClassName = deleteVerticesClassName;
    this._draw = draw;
    this._selectedColor = selectedColor;
    this._onDrawModeChange = onDrawModeChange;
  }

  onAdd(map) {
    this._map = map;

    const createButton = (className, title, onClick) => {
      const btn = document.createElement("button");
      btn.className = `mapbox-gl-draw_ctrl-draw-btn ${className || ""}`;
      btn.type = "button";
      btn["aria-label"] = title;
      btn.setAttribute("title", title);

      if (onClick) {
        btn.onclick = onClick;
      }

      return btn;
    };

    const selectBtn = createButton(
      this._selectClassName,
      "Simple Select Mode",
      () => {
        // console.log('select mode click');
        const mode = "simple_select";
        this._draw.changeMode(mode);
        this.setMode(mode);
        this._onDrawModeChange(mode);
      }
    );

    const editBtn = createButton(
      this._editClassName,
      "Edit Mode (Double click a polygon to start edit mode)",
      () => {
        // console.log('edit mode click');
        const mode = "direct_select";
        const selectedIds = this._draw.getSelectedIds();
        if (selectedIds.length > 0) {
          const options = { featureId: selectedIds[0] };
          this._draw.changeMode(mode, options);
          this.setMode(mode);
          this._onDrawModeChange(mode, options);
        }
      }
    );

    const rectangleBtn = createButton(
      "mapbox-gl-draw_polygon",
      "Draw Rectangle Mode",
      () => {
        // console.log('draw rectangle mode click');
        const mode = "draw_rectangle";
        this._draw.changeMode(mode);
        this.setMode(mode);
        this._onDrawModeChange(mode);
      }
    );

    const polygonBtn = createButton(
      this._polygonClassName,
      "Draw Polygon Mode",
      () => {
        // console.log('draw polygon mode click');
        const mode = "draw_polygon";
        this._draw.changeMode(mode);
        this.setMode(mode);
        this._onDrawModeChange(mode);
      }
    );

    const deleteBtn = createButton("mapbox-gl-draw_trash", "Delete", () => {
      // console.log('delete click');
      this.deleteSelected();
    });

    const deleteVerticesBtn = createButton(
      this._deleteVerticesClassName,
      "Delete selected vertices",
      () => {
        this._draw.trash();
      }
    );

    const container = document.createElement("div");
    container.className = "mapboxgl-ctrl-group mapboxgl-ctrl";
    container.appendChild(selectBtn);
    container.appendChild(editBtn);
    container.appendChild(rectangleBtn);
    container.appendChild(polygonBtn);
    container.appendChild(deleteBtn);
    container.appendChild(deleteVerticesBtn);

    this._container = container;
    this._selectBtn = selectBtn;
    this._editBtn = editBtn;
    this._rectangleBtn = rectangleBtn;
    this._polygonBtn = polygonBtn;
    this._deleteBtn = deleteBtn;
    this._deleteVerticesBtn = deleteVerticesBtn;

    this._map.on("draw.modechange", (e) => {
      this.setMode(e.mode);
    });

    this._map.on("draw.selectionchange", (e) => {
      this.toggleDelete(e.features.length > 0);
      this.toggleEditMode(e.features.length > 0);
      this.toggleDeleteVertices(
        this._draw.getSelectedPoints().features.length > 0
      );
    });

    this.setMode(this._draw.getMode());

    const selectedIds = this._draw.getSelectedIds();
    this.toggleEditMode(selectedIds.length > 0);
    this.toggleDelete(selectedIds.length > 0);
    this.toggleDeleteVertices(
      this._draw.getSelectedPoints().features.length > 0
    );

    return this._container;
  }

  deleteSelected() {
    if (this._draw.getMode() === "simple_select") {
      this._draw.trash();
    } else {
      const selectedIds = this._draw.getSelectedIds();

      if (selectedIds.length > 0) {
        // need to manually delete selected features and fire an "draw.delete" event since we aren't in "simple_select" mode and trash only deletes selected points NOT the selected features
        this._draw.delete(selectedIds);
        this._map.fire("draw.delete", {
          type: "FeatureCollection",
          features: selectedIds,
        });
      }

      this.setMode("simple_select");
      this._onDrawModeChange("simple_select");
    }

    this.toggleDelete(false);
  }

  toggleDelete(enabled) {
    const onKeyDown = (e) => {
      // delete key
      if (e.keyCode === 46) {
        this.deleteSelected();
      }
    };

    if (enabled) {
      this._deleteBtn.removeAttribute("disabled");
      this._deleteBtn.style.opacity = 1.0;
      this._map._container.addEventListener("keydown", onKeyDown);
    } else {
      this._deleteBtn.setAttribute("disabled", "disabled");
      this._deleteBtn.style.opacity = 0.4;
      this._map._container.removeEventListener("keydown", onKeyDown);
    }
  }

  toggleDeleteVertices(enabled) {
    const onKeyDown = (e) => {
      // backspace
      if (e.keyCode === 8) {
        this._draw.trash();
      }
    };

    if (enabled) {
      this._deleteVerticesBtn.removeAttribute("disabled");
      this._deleteVerticesBtn.style.opacity = 1.0;
      this._map._container.addEventListener("keydown", onKeyDown);
    } else {
      this._deleteVerticesBtn.setAttribute("disabled", "disabled");
      this._deleteVerticesBtn.style.opacity = 0.4;
      this._map._container.removeEventListener("keydown", onKeyDown);
    }
  }

  toggleEditMode(enabled) {
    if (enabled) {
      this._editBtn.removeAttribute("disabled");
      this._editBtn.style.opacity = 1.0;
    } else {
      this._editBtn.setAttribute("disabled", "disabled");
      this._editBtn.style.opacity = 0.4;
    }
  }

  setMode(mode) {
    // console.log(`setMode=${mode}`);
    if (mode === "draw_rectangle") {
      this._selectBtn.style.backgroundColor = "transparent";
      this._editBtn.style.backgroundColor = "transparent";
      this._rectangleBtn.style.backgroundColor = this._selectedColor;
      this._polygonBtn.style.backgroundColor = "transparent";
      this.toggleDelete(false);
    } else if (mode === "draw_polygon") {
      this._selectBtn.style.backgroundColor = "transparent";
      this._editBtn.style.backgroundColor = "transparent";
      this._rectangleBtn.style.backgroundColor = "transparent";
      this._polygonBtn.style.backgroundColor = this._selectedColor;
      this.toggleDelete(false);
    } else if (mode === "simple_select" || mode === "direct_select") {
      if (mode === "simple_select") {
        this._selectBtn.style.backgroundColor = this._selectedColor;
        this._editBtn.style.backgroundColor = "transparent";
      } else {
        this._selectBtn.style.backgroundColor = "transparent";
        this._editBtn.style.backgroundColor = this._selectedColor;
      }

      this._rectangleBtn.style.backgroundColor = "transparent";
      this._polygonBtn.style.backgroundColor = "transparent";

      const selectedIds = this._draw.getSelectedIds();
      this.toggleEditMode(selectedIds.length > 0);
      this.toggleDelete(selectedIds.length > 0);

      if (selectedIds.length > 0) {
        this.toggleEditMode(true);
        this.toggleDelete(true);
      } else {
        this.toggleEditMode(false);
        this.toggleDelete(false);
      }
    }
  }

  onRemove() {
    this._container.parentNode.removeChild(this._container);
    this.toggleDelete(false);
    this.toggleDeleteVertices(false);
    this.toggleEditMode(false);
    this._map.off("draw.modechange");
    this._map.off("draw.selectionchange");
  }
}
